import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import _ from "lodash";
import {
  CONTRACT_MANAGEMENT_HEADER,
  CONTRACT_MANAGEMENT_ACTIONS_OPTIONS,
} from "../service-management/services-list/provider-services-list.helper";
import { useLocation, useNavigate } from "react-router-dom";
import ProfileHeader from "../../../common/components/profile-header/profile-header-and-breadcrumbs";
import { useSetLoading } from "../../../common/hooks/appearance";
import { AlertComponent } from "../../../common/components";
import ServiceListSearchAndFilters from "../service-management/services-list/provider-service-search-filters";
import "../service-management/services-list/provider-services-list.scss";
import { toggleServiceStatus } from "../../../api/provider-section/provider-service-management";
import ListingTable from "../../../common/components/listing-table/list-table";
import AdminPaginationComponent from "../../../common/components/admin-pagination-component/admin-pagination-component";
import AddTemplatePopup from "./add-template-popup";
import {
  CONTRACT_ACTION_TYPE,
  CONTRACT_MANAGEMENT_TYPE,
  PLANER_DASHBOARD_CONTRACT,
  PROVIDER_DASHBOARD_CONTRACT,
} from "../../../utils/constants";
import PdfPreviewPopup from "./pdf-preview-popup";
import ShareTemplatePopup from "./share-template-popup";
import {
  createContractTemplate,
  deleteContractTemplate,
  getContractTemplate,
  getContractTemplateList,
  getDownloadContractTemplateLink,
  shareContractTemplateToOther,
  updateContractTemplate,
} from "../../../api/provider-section/provider-contract-management";
import moment from "moment";
import { ContractTemplateType } from "./type";
import DeleteTemplatePopup from "./delete-template-popup";
import { HttpStatusCode } from "axios";

interface ContractManagementProps {
  contractType: string;
}
const ContractManagement: React.FC<ContractManagementProps> = ({
  contractType,
}: ContractManagementProps) => {
  const location = useLocation();
  const breadcrumbLinkForList = [
    {
      label: "Contract Templates",
      link: "",
      handleClick: () => {},
      disablePointerEvents: true,
    },
  ];
  const [breadcrumbLink, setBreadcrumbLink] = useState(breadcrumbLinkForList);
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number>(20);
  const [isOpenFilter, setIsOpenFilter] = useState<boolean>(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedTemplateURL, setSelectedTemplateURL] = useState<
    string | null
  >();
  const [searchText, setSearchText] = useState<any>(
    location?.state?.searchText || "",
  );

  const searchRef = useRef<{ clear: () => void }>(null);
  const [isResetSearch, setIsResetSearch] = useState(false);
  const isSearchTriggered = useRef(false);
  const pageSize = 10;
  const [isCreateServiceOpen, setIsCreateServiceOpen] = useState(false);
  const [actionType, setActionType] = useState<
    "create" | "edit" | "view" | "archive"
  >("create");

  const [isAddTemplateOpen, setIsAddTemplateOpen] = useState(false);
  const [templateActionType, setTemplateActionType] = useState(
    CONTRACT_ACTION_TYPE.ADD_TEMPLATE,
  );
  const [isPdfPreviewOpen, setIsPdfPreviewOpen] = useState(false);
  const [isShareTemplateOpen, setIsShareTemplateOpen] = useState(false);
  const [isDeleteTemplateOpen, setIsDeleteTemplateOpen] = useState(false);
  
  const [selectedTemplate, setSelectedTemplate] =
    useState<ContractTemplateType | null>(null);

  const getURL = () => {
    if (contractType === CONTRACT_MANAGEMENT_TYPE.PROVIDER) {
      return PROVIDER_DASHBOARD_CONTRACT;
    } else if (contractType === CONTRACT_MANAGEMENT_TYPE.PRENEED_PLANER) {
      return PLANER_DASHBOARD_CONTRACT;
    } else {
      return "";
    }
  };
  const handleSearch = useCallback(
    _.debounce((text: string) => {
      isSearchTriggered.current = true;
      setCurrentPage(1); // Reset to page 1 on search
      setSearchText(text);
      if (text === "") {
        setIsResetSearch(true);
        setTimeout(() => setIsResetSearch(false), 100);
      }
    }, 500),
    [],
  );

  useEffect(() => {
    if (isPdfPreviewOpen) {
      return;
    }
    const timeOut = setTimeout(() => {
      setSelectedTemplateURL(null);
    }, 500);
    return () => clearTimeout(timeOut);
  }, [isPdfPreviewOpen]);

  const resetSearch = () => {
    setSearchText("");
    if (searchRef.current) {
      searchRef.current.clear();
    }
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setCurrentPage(value);
  };

  useEffect(() => {
    const pageToUse = isSearchTriggered.current ? 1 : currentPage;
    getAllContractTemplate(pageToUse);
    isSearchTriggered.current = false;
  }, [currentPage, searchText]);

  const formattedData = useMemo(() => {
    if (Array.isArray(data)) {
      return data.map((item: any) => {
        return {
          id: item.id,
          name: item?.name,
          version: Number(item.version).toFixed(1),
          updatedAt: moment(item.updated_at).format("DD/MM/YYYY, hh:mm A"),
        };
      });
    }
    return [];
  }, [data]);

  const noResultsTxt = useMemo(() => {
    if (loading) {
      return "Loading services...";
    }

    return "No templates found matching your search criteria.";
  }, [loading, searchText]);

  const downloadPdf = async (url: string, fileName?: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName || new Date().getTime() + ".pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      setAlertMessage("Contract template downloaded successfully");
      setAlertType("success");
      setAlertOpen(true);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      setAlertMessage("Contract template downloaded failed");
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };

  const openPdfViewer = (fileUrl: string | undefined) => {
    setSelectedTemplateURL(fileUrl);
    setIsPdfPreviewOpen(true);
  };

  const shareContractTemplate = async (emails: string[]) => {
    setIsShareTemplateOpen(false);
    setLoading(true);
    try {
      const data = {
        id: selectedTemplate?.id,
        recipients: emails,
      };
      const response: any = await shareContractTemplateToOther(getURL(), data);
      if (response?.status === HttpStatusCode.Ok) {
        setSelectedTemplate(null);
        setAlertMessage(
          response?.data?.data?.data || "Contract template shared successfully",
        );
        setAlertType("success");
        setAlertOpen(true);
      }
    } catch (error: any) {
      let message: any = "Failed to share contract template.";
      const errorArray = error.response?.data?.errors;
      if (errorArray?.length) {
        const values = Object.values(errorArray[0]);
        message = values[0];
      }
      setAlertMessage(message);
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const createTemplate = async (data: FormData) => {
    setLoading(true);
    try {
      const response: any = await createContractTemplate(getURL(), data);
      if (response?.status === HttpStatusCode.Created) {
        setAlertMessage(response?.data?.data?.message);
        setAlertType("success");
        setAlertOpen(true);
        setIsAddTemplateOpen(false);
        getAllContractTemplate(currentPage);
      }
    } catch (error: any) {
      setAlertMessage(
        error.response?.data?.message || "Failed to create contract template.",
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };
  const editTemplate = async (data: FormData) => {
    if (!selectedTemplate) return;
    setIsAddTemplateOpen(false);
    setLoading(true);
    try {
      const response: any = await updateContractTemplate(
        getURL(),
        Number(selectedTemplate.id),
        data,
      );
      if (response?.status === HttpStatusCode.Ok) {
        setAlertMessage(response?.data?.data?.message);
        setAlertType("success");
        setAlertOpen(true);
        setIsAddTemplateOpen(false);
        setSelectedTemplate(null);
        getAllContractTemplate(currentPage);
      }
    } catch (error: any) {
      setAlertMessage(
        error.response?.data?.message || "Failed to edit contract template.",
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };
  const getDownloadableLink = async (id: number, action: string) => {
    setLoading(true);
    try {
      const response: any = await getDownloadContractTemplateLink(getURL(), id);
      if (response?.status === 200) {
        if (action === "View Template") {
          setLoading(false);
          setSelectedTemplateURL(response?.data?.data?.url);
          setIsPdfPreviewOpen(true);
        } else if (action === "Download Template") {
          downloadPdf(
            response?.data?.data?.url,
            response?.data?.data?.fileName,
          );
          setAlertMessage("Contract template download in progress.");
          setAlertType("success");
          setAlertOpen(true);
        }
      } else {
        setLoading(false);
        setAlertMessage("Failed to fetch contract template.");
        setAlertType("error");
        setAlertOpen(true);
      }
    } catch (error: any) {
      setLoading(false);
      setAlertMessage(
        error.response?.data?.message || "Failed to fetch contract template.",
      );
      setAlertType("error");
      setAlertOpen(true);
    }
  };

  const deleteTemplate = async (id?: number) => {
    if (!id) {
      return;
    }
    setLoading(true);
    try {
      const response: any = await deleteContractTemplate(getURL(), id);
      if (response?.status === 200) {
        setAlertMessage(response?.data?.data?.message);
        setAlertType("success");
        setAlertOpen(true);
        getAllContractTemplate(currentPage);
        setSelectedTemplate(null)
      }
    } catch (error: any) {
      setAlertMessage(
        error.response?.data?.message || "Failed to delete template.",
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = (actionLabel: string, id: number, item: any) => {
    setSelectedTemplate(item);
    if (actionLabel === "Edit Template") {
      getSingleTemplate(id);
    } else if (actionLabel === "Delete Template") {
      setIsDeleteTemplateOpen(true);
    } else if (
      actionLabel === "View Template" ||
      actionLabel === "Download Template"
    ) {
      getDownloadableLink(id, actionLabel);
    } else if (actionLabel === "Share Template") {
      setIsShareTemplateOpen(true);
    }
    // Handle other actions like "archive" here
  };

  const getAllContractTemplate = async (pageNumber: number) => {
    setLoading(true);
    try {
      const params: any = {
        page: pageNumber,
        pageSize,
      };
      if (searchText) {
        params.search = searchText;
      }
      const response: any = await getContractTemplateList(getURL(), params);
      if (response?.status === HttpStatusCode.Ok) {
        setData(response?.data?.data?.data);
        setTotalRecords(response?.data?.data?.pagination?.totalCount);
      }
    } catch (error: any) {
      setAlertMessage(
        error.response?.data?.message || "Failed to fetch contract template.",
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const getSingleTemplate = async (id: number) => {
    setLoading(true);
    try {
      const response: any = await getContractTemplate(getURL(), id);
      console.log("templateName", selectedTemplate);
      if (response?.status === HttpStatusCode.Ok) {
        setSelectedTemplate(response?.data?.data?.data);
        setIsAddTemplateOpen(true);
        setTemplateActionType(CONTRACT_ACTION_TYPE.EDIT_TEMPLATE);
      }
    } catch (error: any) {
      setAlertMessage(
        error.response?.data?.message || "Failed to fetch contract template.",
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllContractTemplate(currentPage);
  }, []);

  const handleStatusToggle = async (id: number, newStatus: boolean) => {
    try {
      const response = await toggleServiceStatus(id, {
        isActive: newStatus,
      });

      if (response?.data?.success) {
        setData((prev) =>
          prev.map((item) =>
            item.id === id
              ? {
                  ...item,
                  status: newStatus ? "active" : "inactive",
                }
              : item,
          ),
        );

        setAlertMessage(response?.data?.data?.message);
        setAlertType("success");
        setAlertOpen(true);
      }
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to update service status.",
      );
      setAlertType("error");
      setAlertOpen(true);
    }
  };

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <div className="service-main-wrapper listing-page-main-wrapper">
      <div
        style={{
          display: isCreateServiceOpen ? "none" : "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <ProfileHeader
          breadcrumbLink={breadcrumbLink}
          mode={
            contractType === CONTRACT_MANAGEMENT_TYPE.PROVIDER
              ? "provider"
              : "preNeed"
          }
        />
        <div
          className="list-and-filter-wrapper service-list-and-filter-wrapper"
          style={{
            display: !isViewOpen ? "flex" : "none",
            flexDirection: "column",
            height: "100%",
            paddingBottom: totalRecords > 0 ? "0px" : "10px",
          }}
        >
          <div className="list-search-and-filters service-list-search-and-filters">
            <ServiceListSearchAndFilters
              onSearch={handleSearch}
              showFilter={false}
              isFilterOpen={isOpenFilter}
              searchRef={searchRef}
              isReset={isResetSearch}
              localSearchText={searchText}
              placeholder="Search by Template ID or Template Name"
              actionText="Add Template"
              createServiceHandler={() => {
                setIsAddTemplateOpen(true);
                setTemplateActionType(CONTRACT_ACTION_TYPE.ADD_TEMPLATE);
                //setActionType("create");
              }}
            />
          </div>
          <div className="listing-table-wrapper service-listing-table-wrapper">
            <ListingTable
              headers={CONTRACT_MANAGEMENT_HEADER}
              data={formattedData?.map((item) => ({
                ...item,
                action: "verticalEllipse",
                actionOptions: CONTRACT_MANAGEMENT_ACTIONS_OPTIONS,
              }))}
              onRowClick={(row: any) => {
                // handleRowClick(row?.id);
              }}
              noResultsText={noResultsTxt}
              fieldName={"services-list"}
              handleAction={handleAction}
              actionWidth="175px"
            />
            <AddTemplatePopup
              actionType={templateActionType}
              open={isAddTemplateOpen}
              templateName={selectedTemplate?.name}
              fileUrl={selectedTemplate?.fileUrl}
              fileName={selectedTemplate?.fileName}
              onCancel={() => {
                setIsAddTemplateOpen(false);
                setSelectedTemplate(null);
              }}
              createTemplate={createTemplate}
              editTemplate={editTemplate}
              openPdfViewer={openPdfViewer}
            />
          </div>
          <PdfPreviewPopup
            open={isPdfPreviewOpen}
            onClose={() => {
              setIsPdfPreviewOpen(false);
            }}
            fileUrl={selectedTemplateURL || ""}
          />
          <ShareTemplatePopup
            templateName={selectedTemplate?.name}
            open={isShareTemplateOpen}
            onClose={() => setIsShareTemplateOpen(false)}
            onShare={shareContractTemplate}
          />
          <DeleteTemplatePopup
            open={isDeleteTemplateOpen}
            onClose={() => {
              setIsDeleteTemplateOpen(false);
              setSelectedTemplate(null);
            }}
            onDelete={() => {
              deleteTemplate(selectedTemplate?.id);
              setIsDeleteTemplateOpen(false);
              setSelectedTemplate(null);
            }}
          />

          {totalRecords > 0 && (
            <AdminPaginationComponent
              totalRecords={totalRecords}
              pageSize={pageSize}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </div>

      <AlertComponent
        message={alertMessage}
        type={alertType ?? "info"}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
    </div>
  );
};

export default ContractManagement;
